import { ArrowRight, BadgeCheck, CircleCheck, CircleX, Lightbulb, Shuffle } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { EXAM_BY_ID } from "../data/exams";
import { ALL_QUESTIONS, useBankVersion } from "../data/questions";
import type { Question } from "../data/types";
import { useLang, useT } from "../i18n";
import { dayKey } from "../lib/dates";

const KEYS = "ABCDEF";

/** Câu phù hợp để thử nhanh ở trang chủ: một đáp án, không có code, đề ngắn; ưu tiên câu mẫu chính thức. */
function candidates(): Question[] {
  const fit = ALL_QUESTIONS.filter((q) => q.answer.length === 1 && !q.code && q.prompt.length <= 420 && q.options.every((o) => o.length <= 160));
  const official = fit.filter((q) => q.official);
  return official.length >= 3 ? official : fit;
}

function dayIndex(n: number): number {
  let h = 0;
  for (const ch of dayKey()) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return n ? h % n : 0;
}

/** Thử một câu ngay trên trang chủ — không ghi vào tiến độ. */
export function SampleQuestion() {
  const t = useT();
  const lang = useLang();
  const bankVersion = useBankVersion();
  const pool = useMemo(candidates, [bankVersion]);
  const [offset, setOffset] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const q = pool.length ? pool[(dayIndex(pool.length) + offset) % pool.length] : undefined;

  if (!q) {
    return (
      <div className="card sample-q sample-q-empty" aria-busy="true">
        <div className="skeleton-line w40" /><div className="skeleton-line" /><div className="skeleton-line w80" />
        <div className="skeleton-option" /><div className="skeleton-option" /><div className="skeleton-option" />
      </div>
    );
  }

  const exam = EXAM_BY_ID[q.exam];
  const answered = picked !== null;
  const correct = picked === q.answer[0];
  const explanation = lang === "en" ? q.explanationEn ?? q.explanation : q.explanation;
  const next = () => { setOffset((o) => o + 1); setPicked(null); };

  return (
    <section className="card sample-q" aria-labelledby="sample-q-title">
      <div className="row between sample-q-head">
        <span className="eyebrow" id="sample-q-title">{t("THỬ MỘT CÂU", "TRY A QUESTION")}</span>
        <span className="row tiny">
          <span className="chip" style={{ color: exam.accent }}>{exam.code}</span>
          {q.official && <span className="chip official"><BadgeCheck className="ico" size={13} aria-hidden="true" /> {t("Câu mẫu chính thức", "Official sample")}</span>}
        </span>
      </div>
      <p className="sample-q-prompt" lang="en">{q.prompt}</p>
      <div className="options" role="radiogroup" aria-labelledby="sample-q-title">
        {q.options.map((opt, i) => {
          const isAns = q.answer[0] === i;
          const cls = ["option", "sm", answered ? "locked" : "", answered && isAns ? "correct" : "", answered && picked === i && !isAns ? "incorrect" : ""].filter(Boolean).join(" ");
          return (
            <button key={i} type="button" role="radio" aria-checked={picked === i} className={cls} disabled={answered} onClick={() => setPicked(i)} lang="en">
              <span className="key" aria-hidden="true">{KEYS[i]}</span>
              <span>{opt}</span>
            </button>
          );
        })}
      </div>
      {answered ? (
        <div className="explain" aria-live="polite">
          <div className={`callout ${correct ? "ok" : "bad"}`}>
            <strong className="row-inline">{correct
              ? <><CircleCheck className="ico" size={17} aria-hidden="true" /> {t("Chính xác!", "Correct!")}</>
              : <><CircleX className="ico" size={17} aria-hidden="true" /> {t(`Chưa đúng — đáp án là ${KEYS[q.answer[0]!]}.`, `Not quite — the answer is ${KEYS[q.answer[0]!]}.`)}</>}</strong>{" "}
            <span className="sample-q-expl">{explanation}</span>
          </div>
          {q.takeaway && <p className="callout takeaway small"><Lightbulb className="ico" size={15} aria-hidden="true" /> <strong>{t("Ghi nhớ:", "Remember:")}</strong> {lang === "en" ? q.takeaway.en : q.takeaway.vi}</p>}
          <div className="row">
            <Link to="/diagnostic" className="btn sm primary">{t("Kiểm tra trình độ 12 câu", "12-question placement test")} <ArrowRight className="ico" size={15} aria-hidden="true" /></Link>
            {pool.length > 1 && <button type="button" className="btn sm" onClick={next}><Shuffle className="ico" size={15} aria-hidden="true" /> {t("Câu khác", "Another one")}</button>}
          </div>
        </div>
      ) : (
        <p className="tiny muted mb-0">{t("Đề tiếng Anh như thi thật · chọn một đáp án để xem giải thích.", "English, like the real exam · pick an answer to see the explanation.")}</p>
      )}
    </section>
  );
}
